/**
 * Shared skin primitives. Screens compose these; they never re-implement panel chrome.
 */

import { useEffect, useRef, type ReactNode, type CSSProperties } from 'react'
import { Icon, type IconName } from './Icon'
import { sz, useViewport } from './viewport'

/* ------------------------------------------------------------------- Panel */

export function Panel({
  children,
  tone,
  className = '',
  style,
  flat,
}: {
  children: ReactNode
  tone?: 'gold' | 'danger' | 'police' | 'safe'
  className?: string
  style?: CSSProperties
  flat?: boolean
}) {
  return (
    <div
      className={`${flat ? 'panel-flat' : 'panel'} ${className}`}
      data-tone={tone}
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...style }}
    >
      {children}
    </div>
  )
}

export function PanelHead({
  title,
  icon,
  accent,
  right,
  sub,
}: {
  title: string
  icon?: IconName
  accent?: string
  right?: ReactNode
  sub?: string
}) {
  return (
    <div className="panel-hd">
      {icon && <Icon n={icon} s={13} c={accent ?? 'var(--brass-lit)'} />}
      <span
        className="t-label"
        style={{
          color: accent ?? 'var(--ink)',
          letterSpacing: '.15em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </span>
      {/*
        The subtitle is the first thing to go when a panel gets narrow, and it used to go SILENTLY.

        `overflow: hidden` with no `text-overflow` is a hard cut: "Attacks still land, for a
        quarter" rendered as "Attacks still land, for a quart" and "Any one of these ends it" as
        "Any one of the". Nothing signalled that anything had been removed, so it did not read as a
        narrow panel, it read as copy somebody had failed to finish writing. That is what the owner
        saw and called wording that does not look like a game.

        An ellipsis says a sentence continues; `title` gives it back on hover. `minWidth: 0` is
        required or a flex item will not shrink below its longest word no matter what `flexShrink`
        says.
      */}
      {sub && (
        <span
          className="t-label"
          title={sub}
          style={{
            color: 'var(--ink-dim)',
            fontSize: sz(9),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flexShrink: 1000,
          }}
        >
          {sub}
        </span>
      )}
      <div style={{ flex: 1 }} />
      {right}
    </div>
  )
}

/* -------------------------------------------------------------------- Meter */

export function Meter({
  v,
  max = 100,
  a,
  b,
  h = 5,
}: {
  v: number
  max?: number
  a?: string
  b?: string
  h?: number
}) {
  const pct = Math.max(0, Math.min(100, (v / max) * 100))
  return (
    <div className="meter" style={{ height: h }}>
      <i style={{ width: `${pct}%`, ['--m-a' as string]: a, ['--m-b' as string]: b }} />
    </div>
  )
}

/** The 20-cell Heat bar from the mockups. Cells, not a bar: it reads as instrumentation. */
export function Cells({ v, n = 20, crit }: { v: number; n?: number; crit?: boolean }) {
  const on = Math.round((v / 100) * n)
  return (
    <div className="cells" data-crit={crit || undefined}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} data-on={i < on || undefined} />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------------- Btn */

export function Btn({
  children,
  v,
  size,
  full,
  icon,
  onClick,
  disabled,
  title,
  style,
  id,
}: {
  children?: ReactNode
  v?: 'gold' | 'danger' | 'safe' | 'police' | 'ghost'
  size?: 'sm' | 'lg'
  full?: boolean
  icon?: IconName
  onClick?: () => void
  disabled?: boolean
  title?: string
  style?: CSSProperties
  /** A stable hook for the acceptance harness, on the one control a screen exists for. */
  id?: string
}) {
  return (
    <button
      className="btn"
      id={id}
      data-v={v}
      data-size={size}
      data-full={full || undefined}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={style}
    >
      {icon && <Icon n={icon} s={size === 'sm' ? 11 : 13} />}
      {children}
    </button>
  )
}

/* -------------------------------------------------------------------- Chip */

export function Chip({
  children,
  r,
  k,
  c,
}: {
  children: ReactNode
  r?: string
  k?: string
  c?: string
}) {
  return (
    <span className="chip" data-r={r} data-k={k} style={c ? ({ ['--c' as string]: c } as CSSProperties) : undefined}>
      {children}
    </span>
  )
}

/* --------------------------------------------------------------------- Art */

export function Art({
  src,
  alt,
  scrim,
  focal,
  className = '',
  style,
  children,
}: {
  src: string
  alt: string
  scrim?: 'soft' | 'none'
  focal?: string
  className?: string
  style?: CSSProperties
  children?: ReactNode
}) {
  return (
    <div
      className={`art ${className}`}
      data-scrim={scrim}
      style={{ ['--focal' as string]: focal, ...style }}
    >
      <img src={src} alt={alt} loading="lazy" decoding="async" />
      {/* The scrim is an ::after, so it paints above normal-flow children. Overlay content
          is lifted onto its own layer to stay legible on top of it. */}
      {children != null && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'auto' }}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

export function Face({
  src,
  alt,
  size = 34,
  ring,
  round,
  style,
}: {
  src: string
  alt: string
  size?: number
  ring?: boolean
  round?: boolean
  style?: CSSProperties
}) {
  return (
    <div
      className="face"
      data-ring={ring || undefined}
      data-round={round || undefined}
      style={{ width: size, height: size, ...style }}
    >
      <img src={src} alt={alt} loading="lazy" decoding="async" />
    </div>
  )
}

/* ------------------------------------------------------------------- Stat */

export function Stat({
  label,
  value,
  c,
  sub,
  icon,
  align = 'left',
}: {
  label: string
  value: ReactNode
  c?: string
  sub?: ReactNode
  icon?: IconName
  align?: 'left' | 'center' | 'right'
}) {
  return (
    <div style={{ textAlign: align, minWidth: 0 }}>
      <div
        className="t-label"
        style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {icon && <Icon n={icon} s={10} c="var(--ink-dim)" />}
        {label}
      </div>
      <div className="t-num" style={{ fontSize: 16, color: c ?? 'var(--ink-hi)', marginTop: 1, whiteSpace: 'nowrap' }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: sz(10), color: 'var(--ink-dim)', marginTop: 1 }}>{sub}</div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ Layout */

export function Row({
  children,
  gap = 8,
  style,
  className = '',
}: {
  children: ReactNode
  gap?: number
  style?: CSSProperties
  className?: string
}) {
  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', gap, minWidth: 0, ...style }}>
      {children}
    </div>
  )
}

export function Col({
  children,
  gap = 8,
  style,
  className = '',
}: {
  children: ReactNode
  gap?: number
  style?: CSSProperties
  className?: string
}) {
  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap, minWidth: 0, minHeight: 0, ...style }}>
      {children}
    </div>
  )
}

export const Spacer = () => <div style={{ flex: 1 }} />

/* ----------------------------------------------------------------- Screen */

/*
 * A SCREEN IS A MAIN PANEL AND ITS RAILS, and until Phase 18 every screen laid them out by hand as
 * one flex row with `width: 300, flex: 'none'` on each rail. Fourteen files carried that line. On a
 * 393px phone the rails took the row and the main panel was pushed past the right edge, where
 * `overflow: hidden` made it unreachable rather than scrollable.
 *
 * `Stack` is that row, and under 1024px it is a column that scrolls as one. `Rail` is that side
 * column, and under 1024px it is full width at its natural height, under the main panel unless
 * `order` says otherwise. Nothing about a screen's content changes; only where its panels sit.
 */
export function Stack({
  children,
  style,
  className = '',
}: {
  children: ReactNode
  style?: CSSProperties
  className?: string
}) {
  const { stacked } = useViewport()
  return (
    <div
      className={`screen ${className}`}
      data-stacked={stacked || undefined}
      style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0, minWidth: 0, padding: 8, ...style }}
    >
      {children}
    </div>
  )
}

export function Rail({
  width,
  children,
  gap = 8,
  style,
  className = '',
  order,
}: {
  /** Beside the main panel. Ignored when stacked. */
  width: number
  children: ReactNode
  gap?: number
  style?: CSSProperties
  className?: string
  /** Where the rail goes when stacked. The main panel is order 0; a rail that belongs above it says -1. */
  order?: number
}) {
  const { stacked } = useViewport()
  if (stacked) {
    // `rail-stacked`: a panel that fills the rail beside the main panel (`flex: 1` inline) has
    // nothing to fill under it and collapsed to zero height. The class forces natural height.
    return (
      <Col gap={gap} className={`rail-stacked ${className}`} style={{ flex: 'none', order, ...style }}>
        {children}
      </Col>
    )
  }
  return (
    <Col gap={gap} className={`rail scroll-y ${className}`} style={{ width, flex: 'none', ...style }}>
      {children}
    </Col>
  )
}

/** The main panel's frame: fills beside rails, natural height under them. Spread it onto the panel. */
export function mainFrame(stacked: boolean): CSSProperties {
  return stacked
    ? { flex: 'none', minWidth: 0 }
    : { flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }
}

/* ------------------------------------------------------------------- Tabs */

/**
 * A tab strip scrolls when it overflows, and now it SAYS so: an edge fade on whichever side has
 * tabs past it, and the active tab scrolled into view when it changes. The Ledger's eight tabs and
 * Jobs' twelve districts were both cut at the panel edge with nothing to show the rest existed.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { id: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const strip = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = strip.current
    const w = wrap.current
    if (!el || !w) return
    const mark = () => {
      const left = el.scrollLeft > 2
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
      if (left) w.dataset.overLeft = ''
      else delete w.dataset.overLeft
      if (right) w.dataset.overRight = ''
      else delete w.dataset.overRight
    }
    mark()
    el.addEventListener('scroll', mark, { passive: true })
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mark)
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', mark)
      ro?.disconnect()
    }
  }, [items.length])

  useEffect(() => {
    const el = strip.current
    if (!el) return
    const on = el.querySelector<HTMLElement>('.tab[data-on]')
    on?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [value])

  return (
    <div className="tabs-wrap" ref={wrap}>
      <div className="tabs" ref={strip}>
        {items.map((it) => (
          <button key={it.id} className="tab" data-on={value === it.id || undefined} onClick={() => onChange(it.id)}>
            {it.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ Empty / load */

export function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'linear-gradient(90deg, #0e1317, #161d23, #0e1317)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.6s linear infinite',
        border: '1px solid var(--line-soft)',
      }}
    />
  )
}
